import React, { useEffect, useRef, useState } from 'react';
import { Info, Youtube, Tv, FileText, Link, FileUp } from 'lucide-react';
import api from '../api/client';
import './WebsiteConfig.css';

interface WebsiteConfigData {
  logoLightDataUrl?: string;
  logoDarkDataUrl?: string;
  faviconDataUrl?: string;
  secondaryColor?: string;
  accentStart?: string;
  accentEnd?: string;
  progressStart?: string;
  progressEnd?: string;
  rulebookType?: 'text' | 'link' | 'pdf';
  rulebook?: string;
  rulebookPdfDataUrl?: string;
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  broadcastChannelId?: string;
  pageVisibility?: {
    home: boolean;
    championships: boolean;
    drivers: boolean;
    hotlaps: boolean;
  };
  enableLogReplay?: boolean;
  simgridApiKey?: string;
  googleCalendar?: {
    serviceAccountJson?: string;
    calendarId?: string;
  };
}

const DEFAULT_SECONDARY = '#4f46e5';
const DEFAULT_ACCENT_START = '#22d3ee';
const DEFAULT_ACCENT_END = '#34d399';
const DEFAULT_PROGRESS_START = '#10b981';
const DEFAULT_PROGRESS_END = '#22d3ee';

const PRESET_SECONDARY = [
  { label: 'Indigo', value: '#4f46e5' },
  { label: 'Blue', value: '#2563eb' },
  { label: 'Sky', value: '#0ea5e9' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Rose', value: '#f43f5e' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Pink', value: '#ec4899' },
];

const PRESET_ACCENTS: Array<{ label: string; start: string; end: string }> = [
  { label: 'Cyan → Green', start: '#22d3ee', end: '#34d399' },
  { label: 'Blue → Cyan', start: '#3b82f6', end: '#22d3ee' },
  { label: 'Purple → Pink', start: '#a855f7', end: '#ec4899' },
  { label: 'Orange → Red', start: '#f97316', end: '#ef4444' },
  { label: 'Amber → Orange', start: '#f59e0b', end: '#f97316' },
  { label: 'Green → Emerald', start: '#22c55e', end: '#10b981' },
];

const DEFAULT_PAGE_VISIBILITY = { home: true, championships: true, drivers: true, hotlaps: true };
const PAGE_LABELS: Record<string, string> = { home: 'Home', championships: 'Championships', drivers: 'Drivers', hotlaps: 'Hotlaps' };
const CONFIG_STORAGE_KEY = 'websiteConfigData';

const applyCssVars = (cfg: WebsiteConfigData) => {
  const set = (k: string, v: string) => document.documentElement.style.setProperty(k, v);
  if (cfg.secondaryColor) { set('--secondary-color', cfg.secondaryColor); set('--button-bg', cfg.secondaryColor); set('--button-border', cfg.secondaryColor); }
  if (cfg.accentStart) set('--accent-start', cfg.accentStart);
  if (cfg.accentEnd) set('--accent-end', cfg.accentEnd);
  if (cfg.progressStart) set('--progress-start', cfg.progressStart);
  if (cfg.progressEnd) set('--progress-end', cfg.progressEnd);
};

const WebsiteConfig: React.FC = () => {
  const [logoLightFile, setLogoLightFile] = useState<File | null>(null);
  const [logoDarkFile, setLogoDarkFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoLightPreview, setLogoLightPreview] = useState<string | null>(null);
  const [logoDarkPreview, setLogoDarkPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);

  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY);
  const [accentStart, setAccentStart] = useState(DEFAULT_ACCENT_START);
  const [accentEnd, setAccentEnd] = useState(DEFAULT_ACCENT_END);
  const [progressStart, setProgressStart] = useState(DEFAULT_PROGRESS_START);
  const [progressEnd, setProgressEnd] = useState(DEFAULT_PROGRESS_END);

  const [rulebookType, setRulebookType] = useState<'text' | 'link' | 'pdf'>('text');
  const [rulebook, setRulebook] = useState('');
  const [rulebookPdfFile, setRulebookPdfFile] = useState<File | null>(null);
  const [rulebookPdfDataUrl, setRulebookPdfDataUrl] = useState<string | null>(null);

  const [broadcastPlatform, setBroadcastPlatform] = useState<'youtube' | 'twitch' | ''>('');
  const [broadcastChannel, setBroadcastChannel] = useState('');
  const [broadcastChannelId, setBroadcastChannelId] = useState('');
  const [pageVisibility, setPageVisibility] = useState(DEFAULT_PAGE_VISIBILITY);
  const [enableLogReplay, setEnableLogReplay] = useState(false);
  const [simgridApiKey, setSimgridApiKey] = useState('');
  const [gcalServiceAccountJson, setGcalServiceAccountJson] = useState('');
  const [gcalCalendarId, setGcalCalendarId] = useState('');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const pdfInputRef = useRef<HTMLInputElement>(null);

  const loadPreview = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => (typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Read failed')));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const applyConfig = (parsed: WebsiteConfigData) => {
    if (parsed.logoLightDataUrl) setLogoLightPreview(parsed.logoLightDataUrl);
    if (parsed.logoDarkDataUrl) setLogoDarkPreview(parsed.logoDarkDataUrl);
    if (parsed.faviconDataUrl) setFaviconPreview(parsed.faviconDataUrl);
    const next: WebsiteConfigData = {
      secondaryColor: parsed.secondaryColor ?? DEFAULT_SECONDARY,
      accentStart: parsed.accentStart ?? DEFAULT_ACCENT_START,
      accentEnd: parsed.accentEnd ?? DEFAULT_ACCENT_END,
      progressStart: parsed.progressStart ?? DEFAULT_PROGRESS_START,
      progressEnd: parsed.progressEnd ?? DEFAULT_PROGRESS_END,
    };
    setSecondaryColor(next.secondaryColor!);
    setAccentStart(next.accentStart!);
    setAccentEnd(next.accentEnd!);
    setProgressStart(next.progressStart!);
    setProgressEnd(next.progressEnd!);
    applyCssVars(next);
    if (parsed.rulebookType) setRulebookType(parsed.rulebookType);
    if (parsed.rulebook !== undefined) setRulebook(parsed.rulebook);
    if (parsed.rulebookPdfDataUrl) setRulebookPdfDataUrl(parsed.rulebookPdfDataUrl);
    if (parsed.broadcastPlatform !== undefined) setBroadcastPlatform(parsed.broadcastPlatform);
    if (parsed.broadcastChannel !== undefined) setBroadcastChannel(parsed.broadcastChannel);
    if (parsed.broadcastChannelId !== undefined) setBroadcastChannelId(parsed.broadcastChannelId);
    if (parsed.pageVisibility) setPageVisibility({ ...DEFAULT_PAGE_VISIBILITY, ...parsed.pageVisibility });
    if (parsed.enableLogReplay !== undefined) setEnableLogReplay(parsed.enableLogReplay);
    if (parsed.simgridApiKey !== undefined) setSimgridApiKey(parsed.simgridApiKey);
    if (parsed.googleCalendar?.serviceAccountJson !== undefined) setGcalServiceAccountJson(parsed.googleCalendar.serviceAccountJson);
    if (parsed.googleCalendar?.calendarId !== undefined) setGcalCalendarId(parsed.googleCalendar.calendarId);
  };

  useEffect(() => {
    api.getWebsiteConfig()
      .then((res) => applyConfig(res.data?.config || {}))
      .catch(() => {
        const saved = window.localStorage.getItem(CONFIG_STORAGE_KEY);
        if (saved) try { applyConfig(JSON.parse(saved)); } catch { /* ignore */ }
      });
  }, []);

  const saveConfig = async () => {
    let lightDataUrl = logoLightPreview;
    let darkDataUrl = logoDarkPreview;
    let faviconDataUrl = faviconPreview;
    let pdfDataUrl = rulebookPdfDataUrl;

    if (logoLightFile) { lightDataUrl = await loadPreview(logoLightFile); setLogoLightPreview(lightDataUrl); }
    if (logoDarkFile) { darkDataUrl = await loadPreview(logoDarkFile); setLogoDarkPreview(darkDataUrl); }
    if (faviconFile) { faviconDataUrl = await loadPreview(faviconFile); setFaviconPreview(faviconDataUrl); }
    if (rulebookPdfFile) { pdfDataUrl = await loadPreview(rulebookPdfFile); setRulebookPdfDataUrl(pdfDataUrl); }

    const config: WebsiteConfigData = {
      logoLightDataUrl: lightDataUrl ?? undefined,
      logoDarkDataUrl: darkDataUrl ?? undefined,
      faviconDataUrl: faviconDataUrl ?? undefined,
      secondaryColor,
      accentStart,
      accentEnd,
      progressStart,
      progressEnd,
      rulebookType,
      rulebook,
      rulebookPdfDataUrl: pdfDataUrl ?? undefined,
      broadcastPlatform,
      broadcastChannel,
      broadcastChannelId,
      pageVisibility,
      enableLogReplay,
      simgridApiKey: simgridApiKey || undefined,
      googleCalendar: (gcalServiceAccountJson || gcalCalendarId) ? {
        serviceAccountJson: gcalServiceAccountJson || undefined,
        calendarId: gcalCalendarId || undefined,
      } : undefined,
    };

    window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));

    api.saveWebsiteConfig(config)
      .then(() => {
        applyCssVars(config);
        setSaveMessage('Website configuration saved.');
        setTimeout(() => setSaveMessage(null), 3000);
      })
      .catch(() => {
        applyCssVars(config);
        setSaveMessage('Saved locally (backend unavailable).');
        setTimeout(() => setSaveMessage(null), 3000);
      });
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFile: (f: File | null) => void,
    setPreview: (s: string | null) => void,
  ) => {
    const file = event.target.files?.[0] || null;
    setFile(file);
    if (file) setPreview(await loadPreview(file));
  };

  const handlePdfChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setRulebookPdfFile(file);
    if (file) setRulebookPdfDataUrl(await loadPreview(file));
  };

  const togglePage = (key: keyof typeof pageVisibility) =>
    setPageVisibility((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="website-config-page">
      <div className="wc-page-header">
        <h2>Website Config</h2>
        <p>Configure your public site's branding, defaults, and visible pages.</p>
      </div>

      {/* ── Branding ───────────────────────────────────────────────── */}
      <section className="wc-section">
        <h3>Branding</h3>

        <div className="wc-field-group">
          <div className="wc-row">
            <label className="wc-field">
              <span>Light theme logo</span>
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => handleFileChange(e, setLogoLightFile, setLogoLightPreview)} />
            </label>
            <label className="wc-field">
              <span>Dark theme logo</span>
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => handleFileChange(e, setLogoDarkFile, setLogoDarkPreview)} />
            </label>
            <label className="wc-field">
              <span>Favicon</span>
              <input type="file" accept="image/png,image/x-icon,image/svg+xml" onChange={(e) => handleFileChange(e, setFaviconFile, setFaviconPreview)} />
            </label>
          </div>

          <div className="wc-logo-previews">
            {logoLightPreview && (
              <div className="wc-logo-card wc-logo-light-bg">
                <span>Light mode</span>
                <img src={logoLightPreview} alt="Light logo preview" />
              </div>
            )}
            {logoDarkPreview && (
              <div className="wc-logo-card wc-logo-dark-bg">
                <span>Dark mode</span>
                <img src={logoDarkPreview} alt="Dark logo preview" />
              </div>
            )}
            {faviconPreview && (
              <div className="wc-logo-card">
                <span>Favicon</span>
                <img src={faviconPreview} alt="Favicon preview" className="wc-favicon-preview" />
              </div>
            )}
          </div>
        </div>

        {/* Secondary / accent color */}
        <div className="wc-field-group wc-color-group">
          <div className="wc-color-section-label">Secondary / accent color</div>
          <div className="wc-color-row">
            <div className="wc-color-presets">
              {PRESET_SECONDARY.map((p) => (
                <button key={p.value} type="button"
                  className={`wc-color-swatch${secondaryColor === p.value ? ' selected' : ''}`}
                  style={{ background: p.value }} title={p.label}
                  onClick={() => setSecondaryColor(p.value)} />
              ))}
            </div>
            <label className="wc-custom-color-label">
              <span>Custom</span>
              <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="wc-color-native" />
              <span className="wc-color-hex">{secondaryColor}</span>
            </label>
          </div>
          <div className="wc-color-preview-bar" style={{ background: secondaryColor }} />
        </div>

        {/* Card accent gradient */}
        <div className="wc-field-group wc-color-group">
          <div className="wc-color-section-label">Server card accent stripe gradient</div>
          <div className="wc-gradient-presets">
            {PRESET_ACCENTS.map((p) => (
              <button key={p.label} type="button"
                className={`wc-gradient-swatch${accentStart === p.start && accentEnd === p.end ? ' selected' : ''}`}
                style={{ background: `linear-gradient(90deg, ${p.start}, ${p.end})` }}
                title={p.label}
                onClick={() => { setAccentStart(p.start); setAccentEnd(p.end); }} />
            ))}
          </div>
          <div className="wc-gradient-custom-row">
            <label className="wc-custom-color-label">
              <span>Start</span>
              <input type="color" value={accentStart} onChange={(e) => setAccentStart(e.target.value)} className="wc-color-native" />
              <span className="wc-color-hex">{accentStart}</span>
            </label>
            <label className="wc-custom-color-label">
              <span>End</span>
              <input type="color" value={accentEnd} onChange={(e) => setAccentEnd(e.target.value)} className="wc-color-native" />
              <span className="wc-color-hex">{accentEnd}</span>
            </label>
          </div>
          <div className="wc-color-preview-bar" style={{ background: `linear-gradient(90deg, ${accentStart}, ${accentEnd})` }} />
        </div>

        {/* Progress bar gradient */}
        <div className="wc-field-group wc-color-group">
          <div className="wc-color-section-label">Session progress bar gradient</div>
          <div className="wc-gradient-presets">
            {PRESET_ACCENTS.map((p) => (
              <button key={p.label} type="button"
                className={`wc-gradient-swatch${progressStart === p.start && progressEnd === p.end ? ' selected' : ''}`}
                style={{ background: `linear-gradient(90deg, ${p.start}, ${p.end})` }}
                title={p.label}
                onClick={() => { setProgressStart(p.start); setProgressEnd(p.end); }} />
            ))}
          </div>
          <div className="wc-gradient-custom-row">
            <label className="wc-custom-color-label">
              <span>Start</span>
              <input type="color" value={progressStart} onChange={(e) => setProgressStart(e.target.value)} className="wc-color-native" />
              <span className="wc-color-hex">{progressStart}</span>
            </label>
            <label className="wc-custom-color-label">
              <span>End</span>
              <input type="color" value={progressEnd} onChange={(e) => setProgressEnd(e.target.value)} className="wc-color-native" />
              <span className="wc-color-hex">{progressEnd}</span>
            </label>
          </div>
          <div className="wc-color-preview-bar" style={{ background: `linear-gradient(90deg, ${progressStart}, ${progressEnd})` }} />
        </div>
      </section>

      {/* ── Rulebook ───────────────────────────────────────────────── */}
      <section className="wc-section">
        <h3>Rulebook</h3>
        <p className="wc-section-desc">
          Default rulebook for all championships. Can be overridden per championship in the Championship Manager.
        </p>

        <div className="wc-rulebook-type-toggle">
          <button type="button" className={`wc-type-btn${rulebookType === 'text' ? ' active' : ''}`} onClick={() => setRulebookType('text')}>
            <FileText size={14} /> Raw text
          </button>
          <button type="button" className={`wc-type-btn${rulebookType === 'link' ? ' active' : ''}`} onClick={() => setRulebookType('link')}>
            <Link size={14} /> Link
          </button>
          <button type="button" className={`wc-type-btn${rulebookType === 'pdf' ? ' active' : ''}`} onClick={() => setRulebookType('pdf')}>
            <FileUp size={14} /> PDF upload
          </button>
        </div>

        {rulebookType === 'text' && (
          <textarea className="wc-rulebook" placeholder="Enter your rulebook here (Markdown supported)..." value={rulebook} onChange={(e) => setRulebook(e.target.value)} rows={12} />
        )}

        {rulebookType === 'link' && (
          <label className="wc-field">
            <span>Rulebook URL</span>
            <input type="text" className="wc-text-input" placeholder="https://docs.google.com/..." value={rulebook} onChange={(e) => setRulebook(e.target.value)} />
          </label>
        )}

        {rulebookType === 'pdf' && (
          <div className="wc-pdf-upload">
            <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handlePdfChange} />
            <button type="button" className="wc-type-btn active" onClick={() => pdfInputRef.current?.click()}>
              <FileUp size={14} /> {rulebookPdfDataUrl ? 'Replace PDF' : 'Choose PDF'}
            </button>
            {rulebookPdfDataUrl && (
              <span className="wc-pdf-ready">PDF ready — will open in a new window on the public site.</span>
            )}
          </div>
        )}
      </section>

      {/* ── Broadcasting ───────────────────────────────────────────── */}
      <section className="wc-section">
        <h3>Broadcasting</h3>
        <p className="wc-section-desc">
          Set your streaming channel. When a championship is marked as broadcasting, a link appears on the live server card and championship pages.
        </p>
        <div className="wc-broadcast-row">
          <div className="wc-platform-toggle">
            <button type="button" className={`wc-platform-btn${broadcastPlatform === 'youtube' ? ' active' : ''}`} onClick={() => setBroadcastPlatform(broadcastPlatform === 'youtube' ? '' : 'youtube')}>
              <Youtube size={16} /> YouTube
            </button>
            <button type="button" className={`wc-platform-btn${broadcastPlatform === 'twitch' ? ' active' : ''}`} onClick={() => setBroadcastPlatform(broadcastPlatform === 'twitch' ? '' : 'twitch')}>
              <Tv size={16} /> Twitch
            </button>
          </div>
          {broadcastPlatform && (
            <div className="wc-channel-fields">
              <label className="wc-field wc-channel-field">
                <span>Channel name</span>
                <div className="wc-channel-input-row">
                  <input type="text" className="wc-text-input" placeholder={broadcastPlatform === 'youtube' ? 'e.g. @MyChannel' : 'e.g. mychannelname'} value={broadcastChannel} onChange={(e) => setBroadcastChannel(e.target.value)} />
                  <span className="wc-info-tip" title="Used to build the Watch Live link on championship pages.">
                    <Info size={15} />
                  </span>
                </div>
              </label>
              {broadcastPlatform === 'youtube' && (
                <label className="wc-field wc-channel-field">
                  <span>YouTube Channel ID <span style={{ color: '#94a3b8', fontWeight: 400 }}>(for live embed)</span></span>
                  <div className="wc-channel-input-row">
                    <input type="text" className="wc-text-input" placeholder="e.g. UC9csiS6q_C3C0FYhyr1rnFA" value={broadcastChannelId} onChange={(e) => setBroadcastChannelId(e.target.value)} />
                    <span className="wc-info-tip" title="Your Channel ID is required for the live stream embed — handles (@name) don't work in iframes. Find it: YouTube Studio → Settings → Channel → Basic Info, or on your channel's About page click Share → Copy channel ID. It starts with UC…">
                      <Info size={15} />
                    </span>
                  </div>
                </label>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Page Visibility ────────────────────────────────────────── */}
      <section className="wc-section">
        <h3>Page Visibility</h3>
        <p className="wc-section-desc">Choose which pages appear in the public frontend navigation.</p>
        <div className="wc-page-toggles">
          {(Object.keys(pageVisibility) as Array<keyof typeof pageVisibility>).map((key) => (
            <label key={key} className="wc-toggle-row">
              <span className="wc-toggle-label">{PAGE_LABELS[key]}</span>
              <button type="button" role="switch" aria-checked={pageVisibility[key]}
                className={`wc-toggle${pageVisibility[key] ? ' on' : ''}`}
                onClick={() => togglePage(key)}>
                <span className="wc-toggle-thumb" />
              </button>
            </label>
          ))}
        </div>
      </section>

      {/* ── Admin Features ─────────────────────────────────────────── */}
      <section className="wc-section">
        <h3>Admin Features</h3>
        <p className="wc-section-desc">Enable or disable admin tools. Disabled tools are hidden from the navigation bar.</p>
        <div className="wc-page-toggles">
          <label className="wc-toggle-row">
            <div className="wc-toggle-label-group">
              <span className="wc-toggle-label">Log Replay</span>
              <span className="wc-toggle-sublabel">Tool for replaying and debugging server log files</span>
            </div>
            <button type="button" role="switch" aria-checked={enableLogReplay}
              className={`wc-toggle${enableLogReplay ? ' on' : ''}`}
              onClick={() => setEnableLogReplay((v) => !v)}>
              <span className="wc-toggle-thumb" />
            </button>
          </label>
        </div>
      </section>

      {/* ── SimGrid Integration ────────────────────────────────────── */}
      <section className="wc-section">
        <h3>SimGrid Integration</h3>
        <p className="wc-section-desc">
          Connect to <a href="https://www.thesimgrid.com" target="_blank" rel="noreferrer">The SimGrid</a> to import championship schedules and sync entry lists automatically.
          When an API key is set, championships linked to a SimGrid championship ID will sync every 5 minutes.
        </p>
        <label className="wc-field">
          <span>SimGrid API Key</span>
          <input
            type="password"
            className="wc-text-input"
            placeholder="Your SimGrid API key"
            value={simgridApiKey}
            onChange={(e) => setSimgridApiKey(e.target.value)}
            autoComplete="off"
          />
        </label>
      </section>

      {/* ── Google Calendar ────────────────────────────────────────── */}
      <section className="wc-section">
        <h3>Google Calendar Integration</h3>
        <p className="wc-section-desc">
          Automatically push championship rounds and scheduled server starts to a Google Calendar.
          Requires a Google Cloud service account with Calendar API access.
          See the setup instructions below for how to configure this on the Google side.
        </p>
        <details className="wc-instructions">
          <summary>Google Cloud setup instructions</summary>
          <ol className="wc-instructions-list">
            <li>Go to <strong>Google Cloud Console</strong> → APIs &amp; Services → Library and enable the <strong>Google Calendar API</strong>.</li>
            <li>Go to <strong>IAM &amp; Admin</strong> → Service Accounts → Create Service Account. Give it any name (e.g. <em>race-calendar-bot</em>).</li>
            <li>On the service account, click <strong>Keys</strong> → Add Key → Create new key → JSON. Download the JSON file.</li>
            <li>Open <strong>Google Calendar</strong>, find the calendar you want to use (or create a new one), open its settings, and copy the <strong>Calendar ID</strong> (looks like <code>xxx@group.calendar.google.com</code>).</li>
            <li>In that same calendar's settings, scroll to <strong>Share with specific people</strong>, add the service account email (from the JSON file, field <code>client_email</code>), and give it <strong>Make changes to events</strong> permission.</li>
            <li>Paste the full contents of the downloaded JSON file into the field below, and enter the Calendar ID.</li>
          </ol>
        </details>
        <div className="wc-field-group">
          <label className="wc-field">
            <span>Service Account JSON</span>
            <textarea
              className="wc-rulebook"
              placeholder={'Paste the contents of your service account JSON key file here…'}
              value={gcalServiceAccountJson}
              onChange={(e) => setGcalServiceAccountJson(e.target.value)}
              rows={6}
              autoComplete="off"
              spellCheck={false}
              style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
          </label>
          <label className="wc-field">
            <span>Calendar ID</span>
            <input
              type="text"
              className="wc-text-input"
              placeholder="e.g. abc123@group.calendar.google.com"
              value={gcalCalendarId}
              onChange={(e) => setGcalCalendarId(e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>
      </section>

      {/* ── Save ───────────────────────────────────────────────────── */}
      <div className="wc-save-row">
        <button className="primary-button" onClick={saveConfig}>Save website config</button>
        {saveMessage && <span className="wc-save-feedback">{saveMessage}</span>}
      </div>
    </div>
  );
};

export default WebsiteConfig;
